import React, { useState, useMemo } from 'react';
import { DollarSign, Copy, Check } from 'lucide-react';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const FeeCalculator = () => {
  const [mode, setMode] = useState('contingency');

  // Contingency fields
  const [settlementAmount, setSettlementAmount] = useState('');
  const [contingencyPct, setContingencyPct] = useState('');
  const [expenses, setExpenses] = useState('');
  const [expenseDeduction, setExpenseDeduction] = useState('before');

  // Flat fee fields
  const [flatFee, setFlatFee] = useState('');
  const [installments, setInstallments] = useState('');

  const [copied, setCopied] = useState(false);

  const contingencyResult = useMemo(() => {
    const settlement = parseFloat(settlementAmount);
    const pct = parseFloat(contingencyPct) / 100;
    const exp = parseFloat(expenses) || 0;
    if (!settlement || !pct || settlement <= 0 || pct <= 0) return null;

    let attorneyFee, clientShare;
    if (expenseDeduction === 'before') {
      // Expenses deducted before fee calculation
      const netAmount = settlement - exp;
      attorneyFee = netAmount * pct;
      clientShare = netAmount - attorneyFee;
    } else {
      // Fee calculated on gross, then expenses deducted from client's share
      attorneyFee = settlement * pct;
      clientShare = settlement - attorneyFee - exp;
    }

    return { settlement, attorneyFee, clientShare, expenses: exp };
  }, [settlementAmount, contingencyPct, expenses, expenseDeduction]);

  const flatFeeResult = useMemo(() => {
    const fee = parseFloat(flatFee);
    const numInstallments = parseInt(installments);
    if (!fee || fee <= 0) return null;
    if (!numInstallments || numInstallments <= 0) return { total: fee, perInstallment: fee, installments: 1 };

    return {
      total: fee,
      perInstallment: fee / numInstallments,
      installments: numInstallments,
    };
  }, [flatFee, installments]);

  const handleCopy = () => {
    let text;
    if (mode === 'contingency' && contingencyResult) {
      text = `Settlement: ${formatCurrency(contingencyResult.settlement)}\nAttorney Fee: ${formatCurrency(contingencyResult.attorneyFee)}\nExpenses: ${formatCurrency(contingencyResult.expenses)}\nClient Share: ${formatCurrency(contingencyResult.clientShare)}`;
    } else if (mode === 'flatfee' && flatFeeResult) {
      text = `Total Fee: ${formatCurrency(flatFeeResult.total)}\nInstallments: ${flatFeeResult.installments}\nPer Installment: ${formatCurrency(flatFeeResult.perInstallment)}`;
    }
    if (text) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fee Type</label>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden w-full md:w-1/2">
          <button
            onClick={() => setMode('contingency')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'contingency'
                ? 'bg-stone-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Contingency
          </button>
          <button
            onClick={() => setMode('flatfee')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'flatfee'
                ? 'bg-stone-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Flat Fee
          </button>
        </div>
      </div>

      {mode === 'contingency' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Settlement / Award Amount ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={settlementAmount}
                onChange={(e) => setSettlementAmount(e.target.value)}
                placeholder="e.g. 100000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contingency Percentage (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={contingencyPct}
                onChange={(e) => setContingencyPct(e.target.value)}
                placeholder="e.g. 33.33"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expenses / Costs ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={expenses}
                onChange={(e) => setExpenses(e.target.value)}
                placeholder="e.g. 5000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Deduct Expenses</label>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setExpenseDeduction('before')}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    expenseDeduction === 'before'
                      ? 'bg-stone-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Before Fee
                </button>
                <button
                  onClick={() => setExpenseDeduction('after')}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                    expenseDeduction === 'after'
                      ? 'bg-stone-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  After Fee
                </button>
              </div>
            </div>
          </div>

          {contingencyResult && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-3 flex-1">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Settlement</p>
                      <p className="text-lg font-semibold text-gray-700">{formatCurrency(contingencyResult.settlement)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Attorney Fee</p>
                      <p className="text-lg font-semibold text-gray-900 flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-yellow-600" />
                        {formatCurrency(contingencyResult.attorneyFee)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Expenses</p>
                      <p className="text-lg font-semibold text-gray-700">{formatCurrency(contingencyResult.expenses)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Client Share</p>
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(contingencyResult.clientShare)}</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ml-2"
                  title="Copy results"
                >
                  {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Fee ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={flatFee}
                onChange={(e) => setFlatFee(e.target.value)}
                placeholder="e.g. 5000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of Installments</label>
              <input
                type="number"
                min="1"
                step="1"
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                placeholder="e.g. 3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
              />
            </div>
          </div>

          {flatFeeResult && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Per Installment</p>
                    <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <DollarSign className="h-6 w-6 text-yellow-600" />
                      {formatCurrency(flatFeeResult.perInstallment)}
                    </p>
                  </div>
                  <div className="flex gap-8">
                    <div>
                      <p className="text-sm text-gray-600">Total Fee</p>
                      <p className="text-lg font-semibold text-gray-700">{formatCurrency(flatFeeResult.total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Installments</p>
                      <p className="text-lg font-semibold text-gray-700">{flatFeeResult.installments}</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy results"
                >
                  {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FeeCalculator;
